import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Clock, CheckCircle2 } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import {
  currentTenant,
  currentUser,
  agentBySlug,
  agentActivity,
  approvalTasks,
  type AgentStatus,
} from '@/lib/mocks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/states'
import { PageHeader, AgentBadge, ConfidencePill } from '@/lib/atsUi'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/agents/$agent')({
  component: AgentDetailPage,
})

const STATE_META: Record<AgentStatus['state'], { dot: string; label: string; pulse: boolean }> = {
  running: { dot: 'bg-success', label: 'Running now', pulse: true },
  attention: { dot: 'bg-warning', label: 'Needs you', pulse: false },
  idle: { dot: 'bg-muted-foreground/40', label: 'Idle', pulse: false },
}

function AgentDetailPage() {
  const { agent: slug } = Route.useParams()
  const agent = agentBySlug(slug)

  if (!agent) {
    return (
      <DashboardLayout navSections={atsNav} tenant={currentTenant} user={currentUser}>
        <EmptyState title="Agent not found" description="This agent does not exist." />
      </DashboardLayout>
    )
  }

  const meta = STATE_META[agent.state]
  const pending = approvalTasks.filter((t) => t.agent === agent.name)
  const events = agentActivity[agent.name] ?? []

  return (
    <DashboardLayout navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-4">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </Link>
      </div>

      <PageHeader
        title={`${agent.name} agent`}
        subtitle="What this agent has been doing — and anything waiting on you."
      />

      {/* Status card */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <AgentBadge agent={agent.name} />
            <div>
              <div className="flex items-center gap-2">
                <span className={cn('h-2 w-2 rounded-full', meta.dot, meta.pulse && 'animate-pulse')} />
                <span className="text-sm font-medium text-foreground">{meta.label}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{agent.detail}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" asChild>
            <Link to="/settings">Automation settings</Link>
          </Button>
        </CardContent>
      </Card>

      {/* Pending your approval */}
      {pending.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">
            Waiting for your approval ({pending.length})
          </h2>
          <div className="space-y-3">
            {pending.map((task) => (
              <Card key={task.id} className="border-l-4 border-l-warning">
                <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{task.title}</span>
                      <ConfidencePill score={task.confidence} />
                    </div>
                    <p className="text-sm text-muted-foreground">{task.summary}</p>
                  </div>
                  <Button size="sm" className="shrink-0" asChild>
                    <Link to="/inbox">
                      Review
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Activity trail */}
      <section>
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">Recent activity</h2>
        <Card>
          <CardContent className="p-0">
            <ol className="divide-y divide-border">
              {events.map((e) => (
                <li key={e.id} className="flex items-start gap-3 px-4 py-3">
                  <span className="mt-0.5 shrink-0">
                    {e.kind === 'running' ? (
                      <span className="relative flex h-4 w-4 items-center justify-center">
                        <span className="absolute h-2.5 w-2.5 animate-ping rounded-full bg-success/70" />
                        <span className="h-2 w-2 rounded-full bg-success" />
                      </span>
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground leading-snug">{e.action}</p>
                    <p className="text-xs text-muted-foreground">{e.detail}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                    <Clock className="h-3 w-3" />
                    {e.time}
                  </span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </section>
    </DashboardLayout>
  )
}
